import { type Component } from 'vue'
import {
  FilterLinesIcon,
  Settings01Icon,
  FolderIcon,
  PackageIcon,
  File01Icon,
  ActivityIcon,
  CheckCircleIcon,
  UsersUpIcon,
  PresentationChart03Icon,
  Database01Icon,
  Shield01Icon,
  Server01Icon,
  SearchSmIcon
} from '@gohighlevel/ghl-icons/24/outline'
import { AiQueryIcon } from './nav-icons'

// ── Types ─────────────────────────────────────────────────────────────────
export interface NavTool {
  name: string
  path: string
  icon: Component
  description: string
  beta?: boolean
  chipLabel?: string
  adminOnly?: boolean
  costExplorerEditorOnly?: boolean
}

export interface NavSection {
  name: string
  slug: string
  icon: Component
  description: string
  tools: NavTool[]
  /**
   * When set, the sidebar link navigates straight to this route instead of
   * jumping to the section on the launchpad (used by full portals like IDP).
   */
  directPath?: string
}

// ── Catalog ─────────────────────────────────────────────────────────────────
// Single source of truth for the IDP portal's tool catalog. The launchpad
// (Landing.vue) renders every section as a group of cards; the sidebar
// (SidePanel.vue) renders the section list as quick jump-links into it.
export const navSections: NavSection[] = [
  {
    name: 'IDP',
    slug: 'idp',
    icon: Server01Icon,
    description: 'Service catalog — everything about one service on a single screen.',
    directPath: '/idp',
    tools: [
      {
        name: 'Service Catalog',
        path: '/idp/services',
        icon: Server01Icon,
        description:
          'Browse teams and sub-teams, discovered from live metrics, and drill into their services.',
        beta: true
      },
      {
        name: 'Dependency Graph',
        path: '/idp/graph',
        icon: ActivityIcon,
        description:
          'Analyze upstream callers and downstream dependencies for any service, from live metrics.',
        beta: true
      },
      {
        name: 'Shared packages',
        path: '/idp/packages',
        icon: PackageIcon,
        description:
          'Latest published versions of shared packages, read from the repos that distribute them.',
        beta: true
      }
    ]
  },
  {
    name: 'Packages & APIs',
    slug: 'packages-apis',
    icon: PackageIcon,
    description: 'Shared packages, releases, and API contracts.',
    tools: [
      {
        name: 'Package Versions',
        path: '/packages',
        icon: PackageIcon,
        description: 'Track versions of shared packages across services.'
      },
      {
        name: 'API Dependency Graph',
        path: '/dependency-graph',
        icon: ActivityIcon,
        description: 'Explore service-to-service API dependencies.'
      },
      {
        name: 'API Tiering Reports',
        path: '/api-tiering/reports',
        icon: PresentationChart03Icon,
        description: 'Per-team API tier compliance and SLO breach reports.'
      },
      {
        name: 'API Tiering Override',
        path: '/class-latency',
        icon: Settings01Icon,
        description: 'Classify endpoints as interactive or long-running.'
      },
      {
        name: 'Releases',
        path: '/releases',
        icon: File01Icon,
        description: 'Release history and rollout status.'
      }
    ]
  },
  {
    name: 'Versioning',
    slug: 'versioning',
    icon: FolderIcon,
    description: 'Versioned API and event contracts.',
    tools: [
      {
        name: 'API Calls',
        path: '/request-filter',
        icon: FilterLinesIcon,
        description: 'Inspect and filter versioned API requests.'
      },
      {
        name: 'PubSub Events',
        path: '/workers-filter',
        icon: Settings01Icon,
        description: 'Browse versioned PubSub worker events.'
      }
    ]
  },
  {
    name: 'Data',
    slug: 'data',
    icon: Database01Icon,
    description: 'Databases, indexes, and data intelligence.',
    tools: [
      {
        name: 'Mongo Indexes',
        path: '/database/mongo',
        icon: Server01Icon,
        description: 'Index usage and hotspots for MongoDB.'
      },
      {
        name: 'Firestore Indexes',
        path: '/database/firestore',
        icon: File01Icon,
        description: 'Index usage for Firestore collections.'
      },
      {
        name: 'Get by ID',
        path: '/database/db-query',
        icon: SearchSmIcon,
        description: 'Fetch a document directly by its ID.',
        beta: true
      },
      {
        name: 'ClickHouse AI',
        path: '/database/clickhouse-ai/chat',
        icon: AiQueryIcon,
        description: 'Chat and workload advice for ClickHouse.',
        beta: true
      },
      {
        name: 'Atlas Intel',
        path: '/database/atlas-intel',
        icon: Database01Icon,
        description: 'Atlas cost, storage, ops, and health intel.',
        beta: true
      },
      {
        name: 'Firestore Enterprise',
        path: '/database/firestore-estimator',
        icon: Database01Icon,
        description: 'Firestore Enterprise (Mongo-compatible) estimator.',
        beta: true
      }
    ]
  },
  {
    name: 'Quality & Compliance',
    slug: 'quality-compliance',
    icon: CheckCircleIcon,
    description: 'Quality gates and compliance checks.',
    tools: [
      {
        name: 'Overview',
        path: '/quality-gates/overview',
        icon: CheckCircleIcon,
        description: 'Quality gate status at a glance.'
      },
      {
        name: 'PR Results',
        path: '/quality-gates',
        icon: File01Icon,
        description: 'Per-PR quality gate results.'
      },
      {
        name: 'Compliance',
        path: '/quality-gates/compliance',
        icon: PresentationChart03Icon,
        description: 'Compliance checks and reporting.'
      }
    ]
  },
  {
    name: 'Cost',
    slug: 'cost',
    icon: PresentationChart03Icon,
    description: 'Cost visibility and team mapping.',
    tools: [
      {
        name: 'Cost Explorer',
        path: '/cost-explorer',
        icon: PresentationChart03Icon,
        description: 'Explore platform and infrastructure costs.',
        chipLabel: 'New'
      },
      {
        name: 'Teams & Sub-Team Mapping',
        path: '/cost-explorer/teams',
        icon: UsersUpIcon,
        description: 'Map costs to teams and sub-teams.'
      }
    ]
  },
  {
    name: 'Security & IAM',
    slug: 'security-iam',
    icon: Shield01Icon,
    description: 'Access control and audit.',
    tools: [
      {
        name: 'Configuration',
        path: '/iam-config',
        icon: Settings01Icon,
        description: 'IAM configuration and policies.'
      },
      {
        name: 'IAM Sandbox',
        path: '/iam-sandbox',
        icon: Shield01Icon,
        description: 'Compare IAM v1 and v2 authorization decisions side by side.'
      },
      {
        name: 'IDOR Audit',
        path: '/iam-auth-audit',
        icon: Shield01Icon,
        description: 'Team and sub-team IDOR/authz violation rollups.'
      },
      {
        name: 'Audit Logs',
        path: '/audit-logs',
        icon: File01Icon,
        description: 'Searchable platform audit logs.'
      }
    ]
  },
  {
    name: 'Insights',
    slug: 'insights',
    icon: PresentationChart03Icon,
    description: 'Dashboards, initiatives, and reports.',
    tools: [
      {
        name: 'Initiatives',
        path: '/initiatives',
        icon: ActivityIcon,
        description: 'Track cross-team platform initiatives.'
      },
      {
        name: 'Issues',
        path: '/issues',
        icon: FilterLinesIcon,
        description: 'Dev-raised platform issues and their status.'
      },
      {
        name: 'Manager Dashboard',
        path: '/manager-dashboard',
        icon: Server01Icon,
        description: 'Team and manager-level metrics.'
      },
      {
        name: 'Platform Reports',
        path: '/platform-report',
        icon: PresentationChart03Icon,
        description: 'Platform-wide reports and rollups.',
        adminOnly: true
      }
    ]
  },
  {
    name: 'Admin',
    slug: 'admin',
    icon: Settings01Icon,
    description: 'Administrative tooling.',
    tools: [
      {
        name: 'Task Sentinel',
        path: '/task-sentinel',
        icon: UsersUpIcon,
        description: 'Monitor and manage background tasks.',
        adminOnly: true
      }
    ]
  },
  {
    // Pages that have a route but no natural home in the domains above. They
    // were unreachable from the launchpad until they were collected here.
    name: 'Misc',
    slug: 'misc',
    icon: FolderIcon,
    description: 'Tools without a domain of their own.',
    tools: [
      {
        name: 'Operations Dashboard',
        path: '/operations-dashboard',
        icon: PresentationChart03Icon,
        description: 'Support workload, response time, and ownership.'
      },
      {
        name: 'Firestore Monotonic Indexes',
        path: '/database/firestore-intel/monotonic-indexes',
        icon: Database01Icon,
        description: 'Firestore indexes at risk from monotonic write keys.'
      }
    ]
  }
]

// ── Visibility ──────────────────────────────────────────────────────────────
export interface NavAccessContext {
  isAdmin: boolean
  canEditCostExplorer: boolean
}

export function isToolVisible(tool: NavTool, ctx: NavAccessContext): boolean {
  return (
    (!tool.adminOnly || ctx.isAdmin) && (!tool.costExplorerEditorOnly || ctx.canEditCostExplorer)
  )
}

// Sections with their tools filtered by access; empty sections are dropped so
// neither the launchpad nor the sidebar shows a domain the user can't enter.
export function visibleSections(ctx: NavAccessContext): NavSection[] {
  return navSections
    .map((section) => ({ ...section, tools: section.tools.filter((t) => isToolVisible(t, ctx)) }))
    .filter((section) => section.tools.length > 0)
}

/** IDP owns its own landing page and navigation shell. */
export function visibleIdpSection(ctx: NavAccessContext): NavSection | undefined {
  return visibleSections(ctx).find((section) => section.slug === 'idp')
}

/** Domains rendered on the general Developer Tools launchpad. */
export function visiblePlatformSections(ctx: NavAccessContext): NavSection[] {
  return visibleSections(ctx).filter((section) => section.slug !== 'idp')
}
